import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Button,
  message,
  Segmented,
  Typography,
  Tooltip,
  Tabs,
} from "antd";
import {
  PlusOutlined,
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  ReloadOutlined,
  LinkOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  WifiOutlined,
  PrinterOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import {
  exportReservationsPDF,
  exportReservationsCSV,
  printReservations,
} from "../../utils/helpers/exportUtils";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { usePermissions } from "../../utils/usePermissions";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import {
  getReservations,
  getReservationStats,
  getReservationSettings,
  saveReservationSettings,
  createReservation,
  updateReservation,
  updateReservationStatus,
  deleteReservation,
  type Reservation,
  type ReservationStatus,
  type ReservationSettings,
  type GetReservationsParams,
} from "../../utils/api/reservationsAPI";

import {
  setView,
  setCurrentDate,
  setLiveActive,
  setLoading,
  setStatsLoading,
  setStats,
  setSettings,
  setCacheEntry,
  invalidateCache,
  patchReservation,
  removeReservation,
  selectCachedRange,
  type ViewMode,
} from "../../utils/redux/slices/reservationsSlice";

import AddReservationModal from "../../Components/AddReservationModal/AddReservationModal";
import ReservationStats from "./Components/ReservationStats/ReservationStats";
import ReservationFilters from "./Components/ReservationFilters/ReservationFilters";
import ReservationDayView from "./Components/ReservationDayView/ReservationDayView";
import ReservationWeekView from "./Components/ReservationWeekView/ReservationWeekView";
import ReservationMonthView from "./Components/ReservationMonthView/ReservationMonthView";
import ReservationDetails from "./Components/ReservationDetails/ReservationDetails";
import ReservationSettingsModal from "./Components/ReservationSettingsModal/ReservationSettingsModal";
import ReservationInfoModal from "./Components/ReservationInfoModal/ReservationInfoModal";
import VenueEventsSection from "./Components/VenueEventsSection/VenueEventsSection";
import LiveReservationAlert from "./Components/LiveReservationAlert/LiveReservationAlert";

import "./Reservations.scss";

dayjs.extend(isoWeek);

const { Title, Text } = Typography;
const POLL_INTERVAL_MS = 60_000;

const Reservations: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state.userData.user);
  const { hasPermission } = usePermissions();

  // ─── Redux state ─────────────────────────────────────────────────────────
  const view = useSelector((state: any) => state.reservations.view) as ViewMode;
  const currentDateStr = useSelector((state: any) => state.reservations.currentDate) as string;
  const liveActive = useSelector((state: any) => state.reservations.liveActive) as boolean;
  const reservations = useSelector((state: any) => state.reservations.reservations) as Reservation[];
  const stats = useSelector((state: any) => state.reservations.stats);
  const settings = useSelector((state: any) => state.reservations.settings) as ReservationSettings | null;
  const settingsLoaded = useSelector((state: any) => state.reservations.settingsLoaded) as boolean;
  const loading = useSelector((state: any) => state.reservations.loading) as boolean;
  const statsLoading = useSelector((state: any) => state.reservations.statsLoading) as boolean;

  const currentDate = useMemo(() => dayjs(currentDateStr), [currentDateStr]);

  // ─── Permissions ─────────────────────────────────────────────────────────
  const canCreate = hasPermission("reservations:create");
  const canUpdate = hasPermission("reservations:update");
  const canDelete = hasPermission("reservations:delete");
  const canUpdateStatus = hasPermission("reservations:update-status");
  const canManageSettings = hasPermission("reservations:settings");

  const canCreateEvents = hasPermission("venue-events:create");
  const canUpdateEvents = hasPermission("venue-events:update");
  const canDeleteEvents = hasPermission("venue-events:delete");
  const canAddEventReservation = hasPermission("venue-events:add-reservation");
  const canUpdateEventName = hasPermission("venue-events:update-name");
  const canUpdateEventCapacity = hasPermission("venue-events:update-capacity");
  const canUpdateEventTables = hasPermission("venue-events:update-tables");

  const rawClientId = userData?.clientId;
  const clientId: string = typeof rawClientId === "object" ? rawClientId?._id : rawClientId;

  // ─── Filters (local — ephemeral UI state) ────────────────────────────────
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ReservationStatus | "all">("all");

  const filteredReservations = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return reservations;
    return reservations.filter(
      (r) =>
        r.guestName.toLowerCase().includes(term) ||
        r.guestPhone.includes(term) ||
        (r.guestEmail && r.guestEmail.toLowerCase().includes(term))
    );
  }, [reservations, search]);

  // ─── Modals (local — ephemeral UI state) ─────────────────────────────────
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editData, setEditData] = useState<Reservation | null>(null);
  const [savingReservation, setSavingReservation] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"tables" | "events">("tables");
  const [liveAlertReservations, setLiveAlertReservations] = useState<Reservation[]>([]);

  // ─── Date range ───────────────────────────────────────────────────────────
  const { from, to } = useMemo(() => {
    if (view === "day") {
      return { from: currentDate.format("YYYY-MM-DD"), to: currentDate.format("YYYY-MM-DD") };
    }
    if (view === "week") {
      return {
        from: currentDate.startOf("isoWeek").format("YYYY-MM-DD"),
        to: currentDate.endOf("isoWeek").format("YYYY-MM-DD"),
      };
    }
    return {
      from: currentDate.startOf("month").format("YYYY-MM-DD"),
      to: currentDate.endOf("month").format("YYYY-MM-DD"),
    };
  }, [view, currentDate]);

  // Cache key — includes filters so results are correct when filters change
  const cacheKey = useMemo(
    () => `${from}|${to}|${statusFilter}`,
    [from, to, statusFilter]
  );

  // ─── Fetch reservations (cache-aware) ────────────────────────────────────
  const fetchReservations = useCallback(
    async (force = false) => {
      if (!clientId) return;
      dispatch(setLoading(true));
      try {
        const params: GetReservationsParams = { from, to, limit: 500 };
        if (statusFilter !== "all") params.status = statusFilter;
        const res = await getReservations(clientId, params);
        dispatch(setCacheEntry({ key: cacheKey, reservations: res.data.reservations }));
      } catch {
        message.error(t("Failed to load reservations"));
        dispatch(setLoading(false));
      }
    },
    [clientId, from, to, statusFilter, cacheKey, dispatch, t]
  );

  // ─── Fetch stats ──────────────────────────────────────────────────────────
  const fetchStats = useCallback(async (silent = false) => {
    if (!clientId) return;
    if (!silent) dispatch(setStatsLoading(true));
    try {
      const res = await getReservationStats(
        clientId,
        view === "day" ? currentDate.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")
      );
      dispatch(setStats(res.data));
    } catch {
      // non-critical
    } finally {
      if (!silent) dispatch(setStatsLoading(false));
    }
  }, [clientId, view, currentDate, dispatch]);

  // ─── Fetch settings (once) ───────────────────────────────────────────────
  const fetchSettings = useCallback(async () => {
    if (!clientId || settingsLoaded) return;
    try {
      const res = await getReservationSettings(clientId);
      dispatch(setSettings(res.data));
    } catch {
      // non-critical
    }
  }, [clientId, settingsLoaded, dispatch]);

  // ─── Initial fetches ─────────────────────────────────────────────────────
  const cachedReservations = useSelector((state: any) =>
    selectCachedRange(state, cacheKey)
  );

  // When range, filter, or clientId changes: use cache if fresh, otherwise fetch
  useEffect(() => {
    if (!clientId) return;
    if (cachedReservations) {
      dispatch({ type: "reservations/setReservations", payload: cachedReservations });
    } else {
      fetchReservations();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, clientId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // ─── Live polling ─────────────────────────────────────────────────────────
  const knownIdsRef = useRef<Set<string>>(new Set());
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Ref so poll always sees latest reservations without being a dependency
  const reservationsRef = useRef<Reservation[]>(reservations);
  useEffect(() => { reservationsRef.current = reservations; }, [reservations]);

  const poll = useCallback(async () => {
    if (!clientId) return;
    try {
      const params: GetReservationsParams = { from, to, limit: 500 };
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await getReservations(clientId, params);
      const fetched: Reservation[] = res.data.reservations;

      const newOnes = fetched.filter((r) => !knownIdsRef.current.has(r._id));
      // Always update known IDs with everything fetched
      fetched.forEach((r) => knownIdsRef.current.add(r._id));
      dispatch(setCacheEntry({ key: cacheKey, reservations: fetched }));
      fetchStats(true);

      if (newOnes.length > 0) {
        setLiveAlertReservations((prev) => [...prev, ...newOnes]);
      }
    } catch {
      // silent
    }
  }, [clientId, from, to, statusFilter, cacheKey, dispatch, fetchStats]);

  // Keep pollRef always pointing to latest poll — so interval never needs restart
  const pollRef = useRef(poll);
  useEffect(() => { pollRef.current = poll; }, [poll]);

  // When date range changes while live is on, re-seed from current Redux state
  // so we don't alert for reservations already visible in the list
  useEffect(() => {
    if (liveActive) {
      knownIdsRef.current = new Set(reservationsRef.current.map((r) => r._id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => {
    if (!liveActive) {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      knownIdsRef.current = new Set();
      return;
    }
    // Seed from currently visible reservations so we only alert for truly new ones
    knownIdsRef.current = new Set(reservationsRef.current.map((r) => r._id));
    // Start polling immediately then every POLL_INTERVAL_MS
    pollRef.current();
    pollIntervalRef.current = setInterval(() => pollRef.current(), POLL_INTERVAL_MS);
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    };
  }, [liveActive]); // ← interval starts/stops only when live is toggled

  // Turn off live when leaving the page
  useEffect(() => () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    dispatch(setLiveActive(false));
  }, []);

  // ─── Navigation ───────────────────────────────────────────────────────────
  const navigate = (direction: -1 | 1) => {
    const next =
      view === "day"
        ? currentDate.add(direction, "day")
        : view === "week"
        ? currentDate.add(direction, "week")
        : currentDate.add(direction, "month");
    dispatch(setCurrentDate(next.format("YYYY-MM-DD")));
  };

  // ─── Date label ───────────────────────────────────────────────────────────
  const dateLabel = useMemo(() => {
    if (view === "day") {
      if (currentDate.isSame(dayjs(), "day")) return t("Today") + " · " + currentDate.format("DD MMMM YYYY");
      if (currentDate.isSame(dayjs().add(1, "day"), "day")) return t("Tomorrow") + " · " + currentDate.format("DD MMMM YYYY");
      if (currentDate.isSame(dayjs().subtract(1, "day"), "day")) return t("Yesterday") + " · " + currentDate.format("DD MMMM YYYY");
      return currentDate.format("dddd, DD MMMM YYYY");
    }
    if (view === "week") {
      const ws = currentDate.startOf("isoWeek");
      const we = currentDate.endOf("isoWeek");
      return `${ws.format("DD MMM")} – ${we.format("DD MMM YYYY")}`;
    }
    return currentDate.format("MMMM YYYY");
  }, [view, currentDate, t]);

  // ─── CRUD ─────────────────────────────────────────────────────────────────
  const handleSave = async (values: any) => {
    if (!clientId) return;
    setSavingReservation(true);
    try {
      if (editData) {
        await updateReservation(editData._id, values);
        message.success(t("Reservation updated"));
      } else {
        await createReservation(clientId, values);
        message.success(t("Reservation added"));
      }
      setAddModalOpen(false);
      setEditData(null);
      dispatch(invalidateCache());
      fetchReservations(true);
      fetchStats();
    } catch (err: any) {
      message.error(err?.response?.data?.error || t("Failed to save reservation"));
      throw err;
    } finally {
      setSavingReservation(false);
    }
  };

  const handleStatusChange = async (id: string, status: ReservationStatus) => {
    try {
      await updateReservationStatus(id, status);
      message.success(t("Status updated"));
      // Optimistic update — no re-fetch needed
      dispatch(patchReservation({ id, changes: { status } }));
      if (selectedReservation?._id === id) {
        setSelectedReservation((r) => (r ? { ...r, status } : r));
      }
      fetchStats();
    } catch {
      message.error(t("Failed to update status"));
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteReservation(id);
      message.success(t("Reservation deleted"));
      dispatch(removeReservation(id));
      fetchStats();
    } catch {
      message.error(t("Failed to delete reservation"));
    }
  };

  const handleSaveSettings = async (data: Partial<ReservationSettings>) => {
    if (!clientId) return;
    setSavingSettings(true);
    try {
      const res = await saveReservationSettings(clientId, data);
      dispatch(setSettings(res.data.settings));
      setSettingsOpen(false);
    } finally {
      setSavingSettings(false);
    }
  };

  const openDetails = (r: Reservation) => { setSelectedReservation(r); setDetailsOpen(true); };
  const openEdit = (r: Reservation) => { setEditData(r); setAddModalOpen(true); };
  const handleDayClick = (day: dayjs.Dayjs) => {
    dispatch(setCurrentDate(day.format("YYYY-MM-DD")));
    dispatch(setView("day"));
  };

  // ─── Public link ──────────────────────────────────────────────────────────
  const publicUrl = clientId
    ? `${window.location.origin}/reservations/public?clientId=${clientId}${settings?.restaurantName ? `&name=${encodeURIComponent(settings.restaurantName)}` : ""}`
    : null;

  const copyPublicUrl = () => {
    if (publicUrl) { navigator.clipboard.writeText(publicUrl); message.success(t("Public booking link copied!")); }
  };

  const restaurantTitle = settings?.restaurantName || t("Reservations");
  const tables = settings?.tables || [];

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="reservations-page">
      {/* Header */}
      <div className="res-page-header">
        <div className="res-page-header__left">
          <Title level={3} className="res-page-header__title">
            {restaurantTitle}
          </Title>
          <Text type="secondary" className="res-page-header__sub">
            {t("Restaurant reservation management")}
            {tables.length > 0 && (
              <span className="res-page-header__tables-badge">
                · {tables.filter((t) => t.isActive).length} {t("tables")}
              </span>
            )}
          </Text>
        </div>

        <div className="res-page-header__actions">
          <Tooltip title={t("How it works")}>
            <Button icon={<InfoCircleOutlined />} onClick={() => setInfoOpen(true)}>
              {t("Guide")}
            </Button>
          </Tooltip>

          {canManageSettings && (
            <Tooltip title={t("Restaurant settings")}>
              <Button icon={<SettingOutlined />} onClick={() => setSettingsOpen(true)}>
                {t("Settings")}
              </Button>
            </Tooltip>
          )}

          {publicUrl && (
            <Tooltip title={t("Copy public booking link")}>
              <Button icon={<LinkOutlined />} onClick={copyPublicUrl}>
                {t("Public Link")}
              </Button>
            </Tooltip>
          )}

          {canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => { setEditData(null); setAddModalOpen(true); }}
              className="res-page-header__add-btn"
            >
              {t("New Reservation")}
            </Button>
          )}
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(k) => setActiveTab(k as "tables" | "events")}
        className="res-tabs"
        items={[
          { key: "tables", label: t("Table Reservations") },
          { key: "events", label: t("Events") },
        ]}
      />

      {activeTab === "events" ? (
        <VenueEventsSection
          clientId={clientId}
          canCreate={canCreateEvents}
          canUpdate={canUpdateEvents}
          canUpdateName={canUpdateEventName}
          canUpdateCapacity={canUpdateEventCapacity}
          canUpdateTables={canUpdateEventTables}
          canDelete={canDeleteEvents}
          canAddReservation={canAddEventReservation}
        />
      ) : (
        <>
      {/* Stats */}
      <ReservationStats stats={stats} loading={statsLoading} />

      {/* Toolbar */}
      <div className="res-toolbar">
        <div className="res-toolbar__nav">
          <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} className="res-toolbar__nav-btn" />
          <Button
            className="res-toolbar__today-btn"
            onClick={() => dispatch(setCurrentDate(dayjs().format("YYYY-MM-DD")))}
          >
            {t("Today")}
          </Button>
          <Button icon={<RightOutlined />} onClick={() => navigate(1)} className="res-toolbar__nav-btn" />
        </div>

        <span className="res-toolbar__label">{dateLabel}</span>

        <div className="res-toolbar__right">
          <Tooltip title={t("Refresh")}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => { dispatch(invalidateCache()); fetchReservations(true); fetchStats(); }}
              loading={loading}
            />
          </Tooltip>

          {filteredReservations.length > 0 && (
            <>
              <Tooltip title={t("Print")}>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={() => printReservations(filteredReservations, dateLabel)}
                />
              </Tooltip>
              <Tooltip title={t("Export PDF")}>
                <Button
                  icon={<FilePdfOutlined />}
                  onClick={() => exportReservationsPDF(filteredReservations, dateLabel)}
                />
              </Tooltip>
              <Tooltip title={t("Export Excel")}>
                <Button
                  icon={<FileTextOutlined />}
                  onClick={() => exportReservationsCSV(filteredReservations, dateLabel)}
                />
              </Tooltip>
            </>
          )}

          <Tooltip title={liveActive ? "Zaustavi live ažuriranje" : "Upali live ažuriranje (svaki 1 min)"}>
            <button
              onClick={() => dispatch(setLiveActive(!liveActive))}
              className={`res-live-btn${liveActive ? " res-live-btn--active" : ""}`}
            >
              <WifiOutlined />
              Live
            </button>
          </Tooltip>

          <Segmented
            value={view}
            onChange={(v) => dispatch(setView(v as ViewMode))}
            options={[
              { value: "day", icon: <UnorderedListOutlined />, label: t("Day") },
              { value: "week", icon: <AppstoreOutlined />, label: t("Week") },
              { value: "month", icon: <CalendarOutlined />, label: t("Month") },
            ]}
            className="res-toolbar__segmented"
          />
        </div>
      </div>

      {view === "day" && (
        <ReservationFilters
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          onReset={() => { setSearch(""); setStatusFilter("all"); }}
        />
      )}

      {/* Content */}
      <div className="res-content">
        {view === "day" && (
          <ReservationDayView
            reservations={filteredReservations}
            onCardClick={openDetails}
            onStatusChange={canUpdateStatus ? handleStatusChange : undefined}
            onEdit={canUpdate ? openEdit : undefined}
          />
        )}
        {view === "week" && (
          <ReservationWeekView
            reservations={filteredReservations}
            weekStart={currentDate.startOf("isoWeek")}
            onDayClick={handleDayClick}
            onCardClick={openDetails}
          />
        )}
        {view === "month" && (
          <ReservationMonthView
            reservations={filteredReservations}
            currentMonth={currentDate}
            onDayClick={handleDayClick}
          />
        )}
      </div>

      {/* Modals */}
      {(canCreate || canUpdate) && (
        <AddReservationModal
          isOpen={addModalOpen}
          onClose={() => { setAddModalOpen(false); setEditData(null); }}
          onSave={handleSave}
          editData={editData}
          loading={savingReservation}
          tables={tables}
          existingReservations={reservations}
        />
      )}

      <ReservationDetails
        reservation={selectedReservation}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        onEdit={canUpdate ? openEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        onStatusChange={canUpdateStatus ? handleStatusChange : undefined}
        onReviewSaved={(id, review) => {
          dispatch(patchReservation({ id, changes: { guestReview: review } }));
          setSelectedReservation((r) => (r ? { ...r, guestReview: review } : r));
        }}
      />
        </>
      )}

      {/* Settings and Info modals — always available regardless of active tab */}
      {canManageSettings && (
        <ReservationSettingsModal
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          settings={settings}
          onSave={handleSaveSettings}
          saving={savingSettings}
        />
      )}

      <ReservationInfoModal
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
      />

      {liveAlertReservations.length > 0 && (
        <LiveReservationAlert
          reservations={liveAlertReservations}
          onClose={() => setLiveAlertReservations([])}
          onView={(r) => {
            openDetails(r);
            setLiveAlertReservations([]);
          }}
        />
      )}
    </div>
  );
};

export default Reservations;
